// import { route } from "ziggy-js";
import { useRouter } from "@/helpers/composables/useRouter";
import { useFetch } from "@/helpers/composables/useFetch";
import { useMetaConversionAPI } from "@/helpers/composables/useMetaConversionAPI";
import NProgress from "nprogress";
import { ref } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useSettingsStore } from "@/store/settings";

export function useLists({ router, route }) {
    const { to } = useRouter({
        router,
        route
    });

    let { metaAddToCart } = useMetaConversionAPI();

    const { setNotificationMessage } = useSettingsStore();

    const searchLists = (keyword, { afterFetch, onFetchError }) => {
        const { data } = useFetch(route("endpoint.lists.search", { keyword }),
        {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
            },
            onFetchError: async (ctx) => {
                if (onFetchError && typeof onFetchError === "function") {
                    await onFetchError(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .get()
            .json();
        return data;
    };

    const createList = (list, { afterFetch, onFetchError }) => {
        const { data } = useFetch(route("endpoint.lists.store"), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: async (ctx) => {
                if (onFetchError && typeof onFetchError === "function") {
                    await onFetchError(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .post({
                list
            })
            .json();
        return data;
    };

    const makeCurrentList = (list, options = {}) => {
        router.post(route("endpoint.lists.makeCurrent", list.list_id), null, options);
    };

    const renameList = (list_id, list, { afterFetch, onFetchError }) => {
        const { data } = useFetch(route("endpoint.lists.update", [list_id]), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: async (ctx) => {
                if (onFetchError && typeof onFetchError === "function") {
                    await onFetchError(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .put({
                list
            })
            .json();
        return data;
    };

    const updateListItem = (list_item, { afterFetch, onFetchError }) => {
        const { data } = useFetch(
            route("endpoint.list_item.update", list_item.list_item_id),
            {
                beforeFetch: (ctx) => {
                    NProgress.start();
                    ctx.options = {
                        ...ctx.options,
                        headers: {
                            ...ctx.options.headers,
                            'Accept': 'application/json',
                        }
                    };
                    return ctx;
                },
                afterFetch: async (ctx) => {
                    if (afterFetch && typeof afterFetch === "function") {
                        await afterFetch(ctx);
                    }
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                },
                onFetchError: async (ctx) => {
                    if (onFetchError && typeof onFetchError === "function") {
                        await onFetchError(ctx);
                    }
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                }
            }
        )
            .put({
                list_item
            })
            .json();
        return data;
    };
    //

    const addToListFetching = ref(false);
    const addToList = useDebounceFn(function(item, { afterFetch }){

        if (addToListFetching.value) return;
        const list_item = {
            source: item.source,
            price: item.current_unit_price,
            quantity: item.quantity,
            item_variation_id: item.item_variation_id,
            list_id: item.listId ?? item.list_id
        };

        const { data } = useFetch(route("endpoint.list_item.store"), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }

                if (NProgress.isStarted()) {
                    NProgress.done();
                }
                addToListFetching.value = false; 

                metaAddToCart([ctx.data]);

            },
            onFetchError: (ctx)=>{
                setNotificationMessage({
                    type: "error",
                    message: ctx.data.error
                });
            }
        })
            .post({
                list_item
            })
            .json();

        return data;
    }, 250);

    const deleteList = (list_id, { afterFetch }) => {
        const { data } = useFetch(route("endpoint.lists.destroy", list_id), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .delete()
            .json();
        return data;
    };

    const deleteLists = (lists, { afterFetch }) => {
        const { data } = useFetch(route("endpoint.lists.destroyMany"),
        {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .post({
                ids: lists.map((list) => list.list_id)
            })
            .json();
        return data;
    };

    const deleteListRow = (list_row_id, { afterFetch }) => {
        const { data } = useFetch(
            route("endpoint.list_item.destroy", list_row_id),
            {
                beforeFetch: (ctx) => {
                    NProgress.start();
                    ctx.options = {
                        ...ctx.options,
                        headers: {
                            ...ctx.options.headers,
                            'Accept': 'application/json',
                        }
                    };
                    return ctx;
                },
                afterFetch: async (ctx) => {
                    if (afterFetch && typeof afterFetch === "function") {
                        ctx = await afterFetch(ctx);
                    }
                    if (NProgress.isStarted()) {
                        NProgress.done();
                    }
                    return ctx;
                }
            }
        )
            .delete()
            .json();
        return data;
    };

    const addListItemsToCart = (list_items, { afterFetch }) => {
        const { data } = useFetch(route("endpoint.list_item.storeMany"), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .put({
                list_item: list_items
            })
            .json();
        return data;
    };

    const mergeLists = (lists, { afterFetch, onFetchError }) => {
        const { data } = useFetch(route("endpoint.lists.merge"),
        {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: async (ctx)=>{
                setNotificationMessage({
                    type: "error",
                    message: ctx.data.error
                });
                if (onFetchError && typeof onFetchError === "function") {
                    await onFetchError(ctx);
                }
            }
        })
            .post({
                base_list_id: lists[0].list_id,
                merge_list_ids: lists.slice(1).map((list) => list.list_id)
            })
            .json();
        return data;
    };

    const applyCouponOnList = (coupon, { afterFetch, onFetchError } = {}) => {

        const { data } = useFetch(route("coupon.activate"), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }

                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: (ctx) => {
                if (onFetchError && typeof onFetchError === "function") {
                    onFetchError(ctx);
                }
            }
        })
            .post({     
                coupon: coupon
            })
            .json();

        return data;
    };

    const updateCouponItem = (payload, { afterFetch, onFetchError } = {}) => {
        const { data } = useFetch(route("coupon.update-coupon-item"), {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    await afterFetch(ctx);
                }

                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            },
            onFetchError: (ctx) => {
                console.log("onFetchError", ctx)
                if (onFetchError && typeof onFetchError === "function") {
                    onFetchError(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
            }
        })
            .post(payload)
            .json();

        return data;
    };

    const fetchRestrictions = (payload, { afterFetch }) => {
        const { data } = useFetch(route("web.restrictions.check.order"),
        {
            beforeFetch: (ctx) => {
                NProgress.start();
                ctx.options = {
                    ...ctx.options,
                    headers: {
                        ...ctx.options.headers,
                        'Accept': 'application/json',
                    }
                };
                return ctx;
            },
            afterFetch: async (ctx) => {
                if (afterFetch && typeof afterFetch === "function") {
                    
                    await afterFetch(ctx);
                }
                if (NProgress.isStarted()) {
                    NProgress.done();
                }
                
            },
            onFetchError: ()=>{}
        })
            .post(payload)
            .json();
        return data;
    };

    const filterLists = (filter: { [key: string]: any }) => {
        to({
            newParams: filter, options: {preserveState: true}
        });
    };
    return {
        searchLists,
        addToList,
        createList,
        makeCurrentList,
        renameList,
        deleteList,
        deleteLists,
        deleteListRow,
        addListItemsToCart,
        mergeLists,
        filterLists,
        updateListItem,
        applyCouponOnList,
        updateCouponItem,
        fetchRestrictions
    };
}
